import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import StaticData from './StaticData';
import { classNames } from 'utils';
import { Button } from '@asu/components-core/dist/libCore.es';
import { Modal } from 'react-bootstrap';
import Component from './Component';
import { useMutation } from '@tanstack/react-query';
import isEqual from 'lodash/isEqual';
import useServiceAuth from 'auth/useServiceAuth';
import { setAll } from 'state/slices/templateSlice';
import {
  setChecksheetAll,
  setChecksheetRequirementList,
  setChecksheetName,
} from 'state/slices/checksheetSlice';
import {
  checksheetRequestRecall,
  // checksheetGet,
  checksheetUpdate,
  checksheetUpdateProgress,
  checksheetLock,
} from 'apis/checksheetAPIs';
import { submitKuali } from 'apis/adminAPIs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faCircle,
  faLock,
  faPenToSquare,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import ProgressUpdateForm from './ProgressUpdateForm';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  pointerWithin,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import OrphanedRequirements from './OrphanedRequirements';

const statusCircle = {
  DEPARTMENT: {
    color: 'text-gray-4',
  },
  COLLEGE: {
    color: 'text-gray-7',
  },
  PROVOST: {
    color: 'text-warning',
  },
  DARS: {
    color: 'text-info',
  },
  COMPLETE: {
    color: 'text-success',
  },
};

const ChecksheetForm = ({
  checkId,
  checksheetQuery,
  program,
  isLocked,
  holdsLock,
  isValid,
  validation,
  refetchValidation,
  refetchHistory,
  currentTab,
  setCurrentTab,
}) => {
  const {
    data: checksheetData,
    error: getChecksheetError,
    isError: getChecksheetIsError,
    isSuccess: getChecksheetIsSuccess,
    isPending,
    fetchStatus,
    refetch: refetchChecksheet,
  } = checksheetQuery;
  const { getAccessToken } = useServiceAuth();
  const dispatch = useDispatch();

  const idArray = checkId.split('*');
  const paramAcadSubplan = idArray[3];
  // const owners = [];
  let mainOwner = {};
  let componentIdNames = [];
  const previewUrl = process.env.REACT_APP_DS_BASE_URL + '/checksheet';

  const [subplanName, setSubplanName] = useState('');
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [preventLock, setPreventLock] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showKualiModal, setShowKualiModal] = useState(false);
  const [modalView, setModalView] = useState('');
  const [dataToModal, setDataToModal] = useState({});
  const [templateNotFound, setTemplateNotFound] = useState(false);
  const [activeName, setActiveName] = useState('');
  const [showOverlay, setShowOverlay] = useState(false);
  const [editName, setEditName] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [kualiSubmitting, setKualiSubmitting] = useState(false); // Submission loading state
  const [kualiStatus, setKualiStatus] = useState(''); // Kuali status

  const headerRef = useRef(null);

  const historyRequirements = useSelector(
    (state) => state.history.requirements
  );
  const historySubsections = useSelector((state) => state.history.subsections);
  const historyDeleted = useSelector((state) => state.history.deleted);

  // const [historyRequirements, setHistoryRequirements] = useState({});
  // const [historySubsections, setHistorySubsections] = useState({});
  // const [historyDeleted, setHistoryDeleted] = useState({});

  const role = useSelector((state) => state.user.role);
  const userCollege = useSelector((state) => state.user.college);
  const userDepartment = useSelector((state) => state.user.department);

  const checksheetId = useSelector((state) => state.checksheet.checksheetId);
  const checksheetName = useSelector(
    (state) => state.checksheet.checksheetName
  );
  const year = useSelector((state) => state.checksheet.year);
  const programType = useSelector((state) => state.checksheet.programType);
  const acadplan = useSelector((state) => state.checksheet.acadplan);
  const acadsubplan = useSelector((state) => state.checksheet.acadsubplan);
  const college = useSelector((state) => state.checksheet.college);
  const department = useSelector((state) => state.checksheet.department);
  const checksheetRequirementList = useSelector(
    (state) => state.checksheet.checksheetRequirementList
  );
  const courseLists = useSelector((state) => state.checksheet.courseLists);
  const progress = useSelector((state) => state.checksheet.progress);
  const lockedBy = useSelector((state) => state.checksheet.lockedBy);
  const recalledBy = useSelector((state) => state.checksheet.recalledBy);
  const proposed = useSelector((state) => state.checksheet.proposed);

  const templateId = useSelector((state) => state.template.templateId);
  const templateComponentIds = useSelector(
    (state) => state.template.templateComponentIds
  );
  const templateComponentObjects = useSelector(
    (state) => state.template.templateComponentObjects
  );

  const collegeList = useSelector((state) => state.dataLists.collegeList);

  const previousReview = StaticData.progressReview[progress]?.previousReview;
  const nextReview = StaticData.progressReview[progress]?.nextReview;

  const userIsProvost = role?.includes('PROVOST');
  const userIsDars = role?.includes('DARS');
  const userIsCollege = role?.includes('COLLEGE');
  const isSameCollege = userCollege?.includes(college);
  const userIsDepartment = role?.includes('DEPARTMENT');
  const isSameDepartment = userDepartment?.includes(department);
  const isSameRole = role?.includes(progress);

  const readOnly = !(
    (userIsProvost ||
      ((userIsCollege || userIsDars) && isSameCollege) ||
      (userIsDepartment && isSameDepartment)) &&
    isSameRole
  );

  const degree = useMemo(
    () =>
      !!program
        ? program.degreeDescriptionShort
          ? program.degreeDescriptionShort === 'CERT'
            ? ', Certificate'
            : `, ${program.degreeDescriptionShort}`
          : program.acadPlanType === 'MIN'
            ? ', Minor'
            : null
        : null,
    [program]
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  if (
    templateComponentObjects &&
    Object.keys(templateComponentObjects).length > 0
  ) {
    Object.keys(templateComponentObjects).forEach((key) => {
      // Get the component object using the current key
      const componentObject = templateComponentObjects[key];

      // Parse the details of the component object to extract the componentName
      const details = JSON.parse(componentObject.details);
      const componentId = componentObject.sk;
      const componentName = details.componentName;
      const component = {
        componentId: componentId,
        componentName: componentName,
      };
      // Add componentName to the array
      componentIdNames.push(component);
    });

    // Now componentNames array contains all the component names
  }

  // if (!!program?.owners?.length) {
  //   console.log('Getting owners');
  //   let highestPercent = 0;
  //   program.owners.forEach((programOwner) => {
  //     if (
  //       owners.filter(
  //         (owner) => owner.collegeAcadOrg === programOwner.collegeAcadOrg
  //       ).length === 0
  //     ) {
  //       owners.push({
  //         collegeAcadOrg: programOwner.collegeAcadOrg,
  //         collegeDescription: programOwner.collegeDescription,
  //         departmentAcadOrg: programOwner.departmentAcadOrg,
  //         departmentDescription: programOwner.departmentDescription,
  //       });
  //     }

  //     if (programOwner.percentOwned > highestPercent) {
  //       highestPercent = programOwner.percentOwned;
  //       mainOwner = programOwner;
  //     }
  //   });
  // }

  const handleOpenModal = (view) => {
    setModalView(view);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setModalView('');
    setShowModal(false);
    setShowKualiModal(false);
  };

  const handleOpenKualiModal = () => {
    setShowKualiModal(true);
  };

  const {
    mutate: updateChecksheet,
    error: updateChecksheetError,
    isError: updateChecksheetIsError,
  } = useMutation({
    mutationFn: checksheetUpdate,
    onSuccess: () => {
      console.log('Checksheet updated successfully');
      refetchValidation();
    },
    onError: (error) => {
      console.error('Error updating checksheet:', error);
    },
  });

  const {
    mutate: updateChecksheetProgress,
    // error: updateChecksheetProgressError,
  } = useMutation({
    mutationFn: checksheetUpdateProgress,
    onSuccess: () => {
      setTimeout(() => {
        // lockChecksheet({
        //   id: checkId,
        //   jsonData: { action: 'unlock' },
        //   token,
        // });
        refetchChecksheet();
        refetchValidation();
        refetchHistory();
        setPreventLock(false);
      }, 1000);
    },
    onError: () => {
      setPreventLock(false);
    },
  });

  const {
    mutate: requestChecksheetRecall,
    // error: updateChecksheetProgressError,
  } = useMutation({
    mutationFn: checksheetRequestRecall,
    onSuccess: () => {
      setTimeout(() => {
        refetchHistory();
        refetchChecksheet();
      }, 2000);
    },
    onError: () => {
      setPreventLock(false);
    },
  });

  const {
    mutate: lockChecksheet,
    error: lockChecksheetError,
    isError: lockChecksheetIsError,
  } = useMutation({
    mutationFn: checksheetLock,
    onSuccess: () => {
      console.log('Checksheet locked successfully');
      refetchChecksheet();
      refetchValidation();
      refetchHistory();
      setPreventLock(false);
    },
    onError: () => {
      setPreventLock(false);
    },
  });

  // const { mutate: verifyChecksheet } = useMutation({
  //   mutationFn: checksheetGet,
  // });

  const handleLockUnlock = async (action) => {
    let lockCheck = true;

    setPreventLock(true);

    const jsonData = {
      action,
    };

    const token = await getAccessToken();
    // const verifyCheck = await verifyChecksheet({ id: checkId, token });

    // if (
    //   !!verifyCheck?.lockedBy &&
    //   JSON.parse(verifyCheck.lockedBy).asurite !== asurite
    // ) {
    //   lockCheck = false;
    // }

    if (lockCheck) {
      await lockChecksheet({
        id: checkId,
        jsonData,
        token,
      });
    } else {
      setPreventLock(() => false);
      alert(
        'Another user has locked this checksheet. Refresh the page to see new updates.'
      );
    }

    if (lockChecksheetIsError) setError(lockChecksheetError.message);
  };

  // click Edit button to open modal and edit the data
  const openEditModal = (index, type, details, componentId, subsectionId) => {
    const modalData = {
      index: index,
      type: type,
      componentId: componentId,
      details: details,
      subsectionId: subsectionId,
    };

    setDataToModal(modalData);
  };

  // const receiveDataFromComponent = (data) => {
  //   console.log('Received data from component========:' + JSON.stringify(data));
  //   if (data) {
  //     updateNotes(data.checksheetComponentNotes, data.componentId);
  //   }
  // };

  // Function to update parent data from a child

  const updateData = async (
    index,
    type,
    newData,
    componentId,
    subsectionId,
    oldIndex,
    oldSubsectionId,
    orphan = null
  ) => {
    // console.log(
    //   'updateData: ',
    //   index,
    //   type,
    //   newData,
    //   componentId,
    //   subsectionId,
    //   oldIndex,
    //   oldSubsectionId
    // );

    // console.log('checksheetRequirementList:::::', checksheetRequirementList);

    const subsections = checksheetRequirementList
      ? checksheetRequirementList[componentId]?.subsections
      : {};

    if (index === 9999 && subsections && subsections[subsectionId]) {
      index = subsections[subsectionId].requirements?.length || 0;
    } else if (index === 9999) {
      // console.log('index: ', index);
      index = 0;
    }

    let updatedSubsections = { ...(subsections || {}) };
    // console.log('subsections:', subsections);

    // console.log('newdata::::', newData);

    let updatedDataList = subsections?.[subsectionId]?.requirements
      ? [...subsections[subsectionId].requirements]
      : [];

    if (
      !newData ||
      (typeof newData === 'object' && Object.keys(newData).length === 0) ||
      !isEqual(updatedDataList[index], newData)
    ) {
      if (
        !newData ||
        (typeof newData === 'object' && Object.keys(newData).length === 0)
      ) {
        // if newData is empty (delete data)
        updatedDataList.splice(index, 1);
      } else {
        // if new data is not empty, then update data
        updatedDataList[index] = newData;
      }
    }
    let updatedObject = {
      requirements: updatedDataList,
      subsectionName: subsections?.[subsectionId]?.subsectionName || '',
      notes: subsections?.[subsectionId]?.notes || '',
      hours: subsections?.[subsectionId]?.hours || 0,
      upperDivisionHours: subsections?.[subsectionId]?.upperDivisionHours || 0,
    };

    if (
      updatedObject.requirements.length === 0 &&
      updatedObject.subsectionName.trim() === ''
    ) {
      delete updatedSubsections[subsectionId];
    } else {
      updatedSubsections[subsectionId] = updatedObject;
    }

    //if we move a requirement from one subsection to anther, we want to remove it from the old subsection
    if (oldIndex !== 9999 && oldSubsectionId) {
      updatedDataList = updatedSubsections?.[oldSubsectionId]?.requirements
        ? [...updatedSubsections[oldSubsectionId].requirements]
        : [];

      updatedDataList.splice(oldIndex, 1);
      updatedObject = {
        requirements: updatedDataList,
        subsectionName:
          updatedSubsections?.[oldSubsectionId]?.subsectionName || '',
        notes: updatedSubsections?.[oldSubsectionId]?.notes || '',
        hours: updatedSubsections?.[oldSubsectionId]?.hours || 0,
        upperDivisionHours:
          updatedSubsections?.[oldSubsectionId]?.upperDivisionHours || 0,
      };

      if (
        updatedObject.requirements.length === 0 &&
        updatedObject.subsectionName.trim() === ''
      ) {
        delete updatedSubsections[oldSubsectionId];
      } else {
        updatedSubsections[oldSubsectionId] = updatedObject;
      }
    }

    const next = {};

    let added = false;

    // console.log('newSubsctions========', updatedSubsections);

    if (checksheetRequirementList) {
      for (const id in checksheetRequirementList) {
        if (checksheetRequirementList.hasOwnProperty(id)) {
          const item = checksheetRequirementList[id];
          if (componentId === id) {
            next[componentId] = { subsections: updatedSubsections };
            added = true;
            console.log('add new item to existing componentId: ', componentId);
          } else {
            console.log('add existing item to existing componentId: ', id);
            next[id] = { subsections: item.subsections };
          }
        }
      }
      if (!added) {
        // add new item to new component
        // console.log('add new item to new componentId: ', componentId);
        next[componentId] = { subsections: updatedSubsections };
      }
    } else {
      next[componentId] = { subsections: updatedSubsections }; // if no requirements exist, then this is the first new item in the list
    }

    // console.log('next=========', next);

    const { orphanSection, ...sections } = next;

    if (!!orphan) {
      const orphanSectionSubs = { ...orphanSection.subsections };
      const orphanSubsection = {
        ...orphanSectionSubs['orphanSubsection'],
      };
      const orphanRequirements = [...orphanSubsection.requirements];

      const orphanIndex = orphanRequirements.findIndex(
        (req) => req.requirementId === orphan
      );

      orphanRequirements.splice(orphanIndex, 1);

      orphanSubsection.requirements = orphanRequirements;
      orphanSectionSubs['orphanSubsection'] = orphanSubsection;
      orphanSection.subsections = orphanSectionSubs;
      next['orphanSection'] = orphanSection;
    }

    // console.log('next:::::', next);

    dispatch(setChecksheetRequirementList(next));
    const jsonData = {
      year: year.value,
      templateId: templateId,
      checksheetName: checksheetName,
      programType: programType,
      acadplan: acadplan,
      acadsubplan: acadsubplan,
      college: college,
      department: department,
      componentRequirements: sections,
      courseLists: courseLists,
    };
    const token = await getAccessToken();
    const resp = await updateChecksheet({
      id: checksheetId,
      jsonData: jsonData,
      token: token,
    });
    console.log('resp:::::', resp);
    refetchHistory();

    if (updateChecksheetIsError) setError(updateChecksheetError.message);
  };

  const updateSubsection = async (data, subsectionId, componentId, action) => {
    // console.log('Updating subsectionname...');
    // console.log('subsection data ', data);

    const subsections = checksheetRequirementList
      ? checksheetRequirementList[componentId]?.subsections
      : {};

    const updatedSubsections = { ...(subsections || {}) };

    // console.log('subsections:', subsections);

    const subId = subsectionId !== '999' ? subsectionId : uuidv4();

    const updatedDataList = subsections?.[subId]?.requirements
      ? [...subsections[subId].requirements]
      : [];

    const updatedObject = {
      requirements: updatedDataList,
      subsectionName:
        action === 'subsectionName'
          ? data
          : subsections?.[subId]?.subsectionName || '',
      hours: action === 'hours' ? data : subsections?.[subId]?.hours || 0,
      upperDivisionHours:
        action === 'upperDivisionHours'
          ? data
          : subsections?.[subId]?.upperDivisionHours || 0,
      notes: action === 'notes' ? data : subsections?.[subId]?.notes || '',
    };

    updatedSubsections[subId] = updatedObject;

    // console.log('new subsection data: ', updatedSubsections);

    const next = {};

    let added = false;

    if (checksheetRequirementList) {
      for (const id in checksheetRequirementList) {
        if (checksheetRequirementList.hasOwnProperty(id)) {
          const item = checksheetRequirementList[id];
          if (componentId === id) {
            next[componentId] = { subsections: updatedSubsections };
            added = true;
            // console.log('add new item to existing componentId: ', componentId);
          } else {
            // console.log('add existing item to existing componentId: ', id);
            next[id] = { subsections: item.subsections };
          }
        }
      }
      if (!added) {
        // add new item to new component
        // console.log('add new item to new componentId: ', componentId);
        next[componentId] = { subsections: updatedSubsections };
      }
    } else {
      next[componentId] = { subsections: updatedSubsections }; // if no requirements exist, then this is the first new item in the list
    }

    // console.log('next=========', next);

    const { orphanSection, ...sections } = next;

    dispatch(setChecksheetRequirementList(next));
    const jsonData = {
      year: year.value,
      templateId: templateId,
      checksheetName: checksheetName,
      programType: programType,
      acadplan: acadplan,
      acadsubplan: acadsubplan,
      college: college,
      department: department,
      componentRequirements: sections,
      courseLists: courseLists,
    };

    // console.log(jsonData);
    const token = await getAccessToken();
    updateChecksheet({ id: checksheetId, jsonData: jsonData, token: token });

    if (updateChecksheetIsError) setError(updateChecksheetError.message);
  };

  const handleProgressUpdate = async (message, action, updatedProgress) => {
    const jsonData = {
      ids: [checksheetId],
      progress: updatedProgress,
      action,
      note: message,
    };
    const token = await getAccessToken();

    await updateChecksheetProgress({
      jsonData: jsonData,
      token: token,
    });
  };

  const handleRequestRecall = async (message, action) => {
    const jsonData = {
      action,
      note: message,
    };
    const token = await getAccessToken();

    await requestChecksheetRecall({
      id: checksheetId,
      jsonData: jsonData,
      token: token,
    });
  };

  const handleSubmitById = async (checksheetId, retryAttempt = 0) => {
    try {
      console.log('Submitting...', checksheetId);
      setKualiSubmitting(true);
      setKualiStatus('');

      const token = await getAccessToken();
      const jsonData = { id: checksheetId };
      await submitKuali({ jsonData, token });
      handleOpenKualiModal();

      // Wait 5 seconds before refetching the updated status
      setTimeout(async () => {
        const { data: updatedChecksheetData } = await refetchChecksheet({
          force: true,
        });

        const status = updatedChecksheetData?.kualiStatus;

        if (status === 'SUCCESS' || retryAttempt === 1) {
          setKualiStatus(status);
          setKualiSubmitting(false);
        } else {
          console.warn('First fetch failed, retrying...');
          setTimeout(() => {
            handleSubmitById(checksheetId, 1); // Retry once
          }, 3000);
        }
      }, 5000);
    } catch (error) {
      console.error('Kuali submission error:', error);
      setKualiStatus('ERROR');
      setKualiSubmitting(false);
    }
  };

  // const updateNotes = async (newData, componentId) => {
  //   const requirements =
  //     checksheetRequirementList && checksheetRequirementList[componentId]
  //       ? checksheetRequirementList[componentId].requirements
  //       : [];

  //   const next = {};
  //   const newObj = {
  //     notes: newData,
  //     requirements: requirements,
  //   };

  //   var added = false;

  //   if (checksheetRequirementList) {
  //     for (const id in checksheetRequirementList) {
  //       if (checksheetRequirementList.hasOwnProperty(id)) {
  //         const item = checksheetRequirementList[id];
  //         if (componentId === id) {
  //           next[componentId] = newObj;
  //           added = true;
  //         } else {
  //           next[id] = item;
  //         }
  //       }
  //     }
  //     if (added === false) {
  //       // add new item to new component
  //       next[componentId] = newObj;
  //     }
  //   } else {
  //     next[componentId] = newObj; // if no requirements exist, then this is the first new item in the list
  //   }

  //   dispatch(setChecksheetRequirementList(next));
  //   const jsonData = {
  //     year: year.value,
  //     templateId: templatetemplateId,
  //     checksheetName: checksheetName,
  //     programType: programType,
  //     acadplan: acadplan,
  //     acadsubplan: acadsubplan,
  //     college: college,
  //     department: department,
  //     componentRequirements: next,
  //     courseLists: courseLists,
  //   };
  //   console.log('JSON: ', jsonData);
  //   const token = await getAccessToken();
  //   updateChecksheet({ id: checksheetId, jsonData: jsonData, token: token });

  //   if (updateChecksheetIsError) setError(updateChecksheetError.message);
  // };

  const getRequirementText = (req) => {
    let division = '';
    let checkCourses = '';

    if (req.division?.trim()) {
      division =
        req.division === 'Upper' || req.division === 'Lower'
          ? req.division + ' Division '
          : req.division + '-Level ';
    }

    if (
      req.checkType === 'gpa' &&
      req.checkSubtype === 'courses' &&
      !!req.checkCourses?.length
    ) {
      req.checkCourses.forEach((checkCourse, index, array) => {
        if (index !== 0 && index === array.length - 1) checkCourses += 'and ';

        checkCourses += checkCourse.subject + ' ' + checkCourse.number;

        if (array.length > 2 && index < array.length - 1) checkCourses += ', ';
        else checkCourses += ' ';
      });
    }

    if (req.itemType === 'req_course')
      return `${req.subject} ${req.number}: ${req.topic?.description ?? !!req.proposedTitle ? req.proposedTitle : req.description}`;
    if (req.itemType === 'req_gs') return `${division}${req.gs}`;
    if (req.itemType === 'req_elective')
      return `${req.subject ? req.subject + ' ' : ''}${division}Elective`;
    if (req.itemType === 'req_text')
      return `${division}${req.group ? req.group + ': ' : ''}${req.groupIndex === 'custom_text' ? req.customText : req.text}`;
    if (req.itemType === 'req_course_list')
      return `${division}${courseLists[req.courseListId]?.courseListName}`;
    if (req.itemType === 'req_check') {
      const textDisplay = `${req.checkType === 'gpa' ? 'Check: ' : req.checkType === 'milestone' ? 'Milestone: ' : ''}${
        req.gpa ? `Minimum ${req.gpa} ` : ''
      }${req.checkSubtype === 'asu_cum_gpa' ? 'ASU Cumulative GPA' : ''}${
        req.checkSubtype === 'major_gpa' ? 'Major GPA' : ''
      }${req.checkSubtype === 'subject' ? 'GPA in all ' + req.subject + ' ' + division + 'courses' : ''}${
        req.checkSubtype === 'courses' ? `GPA in ${checkCourses}` : ''
      }${req.milestone ? `${Object.values(req.milestone)[0]}` : ''}`;

      return textDisplay;
    }

    return null;
  };

  const handleSaveNameChange = async () => {
    dispatch(setChecksheetName(nameInput));

    const { orphanSection, ...sections } = checksheetRequirementList;

    const jsonData = {
      year: year.value,
      templateId: templateId,
      checksheetName: nameInput,
      programType: programType,
      acadplan: acadplan,
      acadsubplan: acadsubplan,
      college: college,
      department: department,
      componentRequirements: sections,
      courseLists: courseLists,
    };

    // console.log(jsonData);
    const token = await getAccessToken();
    updateChecksheet({ id: checksheetId, jsonData: jsonData, token: token });

    setEditName(false);
  };

  const handleCancelNameChange = () => {
    setNameInput(checksheetName);
    setEditName(false);
  };

  function handleDragStart(event) {
    let activeIds = event.active.id.split('@');
    let activeReqId = activeIds[0];
    let activeSubsectionId = activeIds[1];
    let activeSectionId = activeIds[2];
    let activeReqName = '';

    const activeReq = checksheetRequirementList[activeSectionId].subsections[
      activeSubsectionId
    ].requirements.find((req) => req.requirementId === activeReqId);
    activeReqName = getRequirementText(activeReq.courses[0]);

    if (activeReq.courses.length > 1) activeReqName += '...';

    setActiveName(activeReqName);
  }

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active && over && active.id !== over.id) {
      let activeIds = active.id.split('@');
      let activeReqId = activeIds[0];
      let activeSubsectionId = activeIds[1];
      let activeSectionId = activeIds[2];
      let overIds = over.id.split('@');
      let overReqId = overIds[0];
      let overSubsectionId = overIds[1];
      let overSectionId = overIds[2];
      let activeReqIndex, overReqIndex;

      if (
        checksheetRequirementList?.[activeSectionId]?.subsections?.[
          activeSubsectionId
        ]?.requirements
      )
        checksheetRequirementList[activeSectionId].subsections[
          activeSubsectionId
        ].requirements.forEach((req, index) => {
          if (req.requirementId === activeReqId) {
            activeReqIndex = index;
          }
        });

      if (
        checksheetRequirementList?.[overSectionId]?.subsections?.[
          overSubsectionId
        ]?.requirements
      )
        checksheetRequirementList[overSectionId].subsections[
          overSubsectionId
        ].requirements.forEach((req, index) => {
          if (req.requirementId === overReqId) {
            overReqIndex = index;
          }
        });

      const updatedRequirementList = { ...checksheetRequirementList };
      const activeSection = {
        ...updatedRequirementList[activeSectionId],
      };
      const activeSectionSubs = { ...activeSection.subsections };
      const overSection = {
        ...updatedRequirementList[overSectionId],
      };
      const overSectionSubs = { ...overSection.subsections };

      if (
        activeSectionId === overSectionId &&
        activeSubsectionId === overSubsectionId
      ) {
        const activeSubsection = {
          ...activeSectionSubs[activeSubsectionId],
        };

        activeSubsection.requirements = arrayMove(
          activeSubsection.requirements,
          activeReqIndex,
          overReqIndex
        );

        activeSectionSubs[activeSubsectionId] = activeSubsection;
        activeSection.subsections = activeSectionSubs;
        updatedRequirementList[activeSectionId] = activeSection;
      } else if (
        activeSectionId === overSectionId &&
        !over?.id.toString().includes('@orphanSubsection@orphanSection')
      ) {
        const activeSubsection = {
          ...activeSectionSubs[activeSubsectionId],
        };
        const overSubsection = {
          ...activeSectionSubs[overSubsectionId],
        };

        if (!Object.keys(overSubsection).length) {
          overSubsection.subsectionName = '';
          overSubsection.requirements = [];
          overSubsection.hours = 0;
          overSubsection.upperDivisionHours = 0;
        }

        const activeRequirements = [...activeSubsection.requirements];
        const overRequirements = [...overSubsection.requirements];

        const [movedRequirement] = activeRequirements.splice(activeReqIndex, 1);

        overRequirements.splice(overReqIndex, 0, movedRequirement);

        activeSubsection.requirements = activeRequirements;
        overSubsection.requirements = overRequirements;

        activeSectionSubs[activeSubsectionId] = activeSubsection;
        activeSectionSubs[overSubsectionId] = overSubsection;

        activeSection.subsections = activeSectionSubs;
        updatedRequirementList[activeSectionId] = activeSection;
      } else if (
        !over?.id.toString().includes('@orphanSubsection@orphanSection')
      ) {
        const activeSubsection = {
          ...activeSectionSubs[activeSubsectionId],
        };
        const overSubsection = {
          ...overSectionSubs[overSubsectionId],
        };

        if (!Object.keys(overSubsection).length) {
          overSubsection.subsectionName = '';
          overSubsection.requirements = [];
          overSubsection.hours = 0;
          overSubsection.upperDivisionHours = 0;
        }

        const activeRequirements = [...activeSubsection.requirements];
        const overRequirements = [...overSubsection.requirements];

        const [movedRequirement] = activeRequirements.splice(activeReqIndex, 1);

        // console.log('Moved requirement', movedRequirement);

        const {
          index,
          section: movedSection,
          subsection: movedSubsection,
          ...cleanedRequirement
        } = movedRequirement;
        overRequirements.splice(overReqIndex, 0, cleanedRequirement);

        activeSubsection.requirements = activeRequirements;
        overSubsection.requirements = overRequirements;

        activeSectionSubs[activeSubsectionId] = activeSubsection;
        activeSection.subsections = activeSectionSubs;
        updatedRequirementList[activeSectionId] = activeSection;

        overSectionSubs[overSubsectionId] = overSubsection;
        overSection.subsections = overSectionSubs;
        updatedRequirementList[overSectionId] = overSection;

        if (activeSectionId === 'orphanSection') {
          const oldSection = {
            ...updatedRequirementList[movedSection],
          };
          const oldSectionSubs = { ...oldSection.subsections };
          const oldSubsection = {
            ...oldSectionSubs[movedSubsection],
          };
          const oldRequirements = [...oldSubsection.requirements];

          const oldIndex = oldRequirements.findIndex(
            (req) => req.requirementId === activeReqId
          );

          oldRequirements.splice(oldIndex, 1);

          oldSubsection.requirements = oldRequirements;
          oldSectionSubs[movedSubsection] = oldSubsection;
          oldSection.subsections = oldSectionSubs;
          updatedRequirementList[movedSection] = oldSection;
        }
      }

      const { orphanSection, ...sections } = updatedRequirementList;

      dispatch(setChecksheetRequirementList(updatedRequirementList));

      const jsonData = {
        year: year.value,
        templateId: templateId,
        checksheetName: checksheetName,
        programType: programType,
        acadplan: acadplan,
        acadsubplan: acadsubplan,
        college: college,
        department: department,
        componentRequirements: sections,
        courseLists: courseLists,
      };

      // console.log(jsonData);

      const token = await getAccessToken();

      updateChecksheet({
        id: checksheetId,
        jsonData,
        token,
      });
    }

    setShowOverlay(false);
    setActiveName('');
  };

  const handleDragOver = (event) => {
    const { active, over } = event;

    if (
      !active ||
      !over ||
      over?.id.toString().includes('@orphanSubsection@orphanSection')
    ) {
      setShowOverlay(true);
      return;
    }

    const activeId = active.id;
    const overId = over.id;

    // Split IDs to get subsection and requirement IDs
    let activeIds = activeId.split('@');
    let activeSubsectionId = activeIds[1];
    let overIds = overId.split('@');
    let overReqId = overIds[0];
    let overSubsectionId = overIds[1];

    if (activeSubsectionId !== overSubsectionId || !overReqId)
      setShowOverlay(true);
    else setShowOverlay(false);
  };

  useEffect(() => {
    setNameInput(checksheetName);
  }, [checksheetName]);

  useEffect(() => {
    if (!!program?.subplans?.length) {
      const foundSubplan = program.subplans.find(
        (subplan) => subplan.acadSubPlanCode === paramAcadSubplan
      );

      if (foundSubplan) {
        setSubplanName(foundSubplan.description);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [program]);

  useEffect(() => {
    if (checkId !== '' && checkId !== null) {
      if (getChecksheetIsError) setError(getChecksheetError.message);
      // console.log('getChecksheetIsSuccess: ', getChecksheetIsSuccess);
      if (getChecksheetIsSuccess) {
        // console.log('Checksheet refetched');

        // if checksheet exist
        if (checksheetData) {
          const jsonDetails = JSON.parse(checksheetData['details']);
          const yearValue = StaticData.yearList.find(
            (obj) => obj.value === jsonDetails.year
          );
          // console.log('yearvalue: ', yearValue);
          // console.log(
          //   'jsonDetails.componentRequirements: ',
          //   jsonDetails.componentRequirements
          // );

          let lockedOrUnlocked = {};

          if (!!checksheetData['lockedBy']) {
            lockedOrUnlocked = JSON.parse(checksheetData['lockedBy']);
          }

          let orphanedRequirements = {};

          if (checksheetData?.templateObject.details) {
            const templateJsonDetails = JSON.parse(
              checksheetData.templateObject['details']
            );
            const templateYearValue = StaticData.yearList.find(
              (obj) => obj.value === templateJsonDetails.year
            );

            dispatch(
              setAll({
                templateId: checksheetData.templateObject['sk'],
                year: templateYearValue,
                templateName: templateJsonDetails.templateName,
                templateSettings: templateJsonDetails.settings,
                templateComponentIds: templateJsonDetails.components,
                templateComponentObjects:
                  checksheetData.templateObject['componentObjects'],
                templateValidationList: templateJsonDetails.validationRules,
                templateTextOptions: templateJsonDetails.textOptions,
                templateMilestones: templateJsonDetails.milestones,
              })
            );

            orphanedRequirements = {
              subsections: {
                orphanSubsection: {
                  requirements: [
                    ...Object.entries(jsonDetails.componentRequirements)
                      .filter(
                        ([key, value]) =>
                          !templateJsonDetails.components.includes(key)
                      )
                      .map(([key, value]) =>
                        Object.entries(value.subsections).map(
                          ([subKey, subValue]) =>
                            subValue.requirements.map((req, index) => ({
                              ...req,
                              section: key,
                              subsection: subKey,
                              index,
                            }))
                        )
                      )
                      .reduce((acc, val) => acc.concat(...val), []),
                    ...Object.entries(jsonDetails.componentRequirements)
                      .filter(([key, value]) =>
                        templateJsonDetails.components.includes(key)
                      )
                      .map(([key, value]) =>
                        Object.entries(value.subsections)
                          .filter(([subKey, subValue]) => {
                            const parsedJSON = JSON.parse(
                              checksheetData.templateObject['componentObjects'][
                                key
                              ].details
                            );

                            if (
                              subKey !== key &&
                              !parsedJSON?.subsections?.[subKey] &&
                              !subValue.subsectionName
                            )
                              return true;
                            else return false;
                          })
                          .map(([subKey, subValue]) =>
                            subValue.requirements.map((req, index) => ({
                              ...req,
                              section: key,
                              subsection: subKey,
                              index,
                            }))
                          )
                      )
                      .reduce((acc, val) => acc.concat(...val), []),
                  ],
                },
              },
            };
          } else setTemplateNotFound(true);

          dispatch(
            setChecksheetAll({
              checksheetId: checksheetData['sk'],
              checksheetName: jsonDetails.checksheetName,
              year: yearValue,
              templateId: jsonDetails.templateId,
              programType: jsonDetails.programType,
              acadplan: jsonDetails.acadplan,
              acadsubplan: jsonDetails.acadsubplan,
              // acadplanDescription: jsonDetails.acadplanDescription,
              college: jsonDetails.college,
              department: jsonDetails.department,
              checksheetRequirementList: {
                ...jsonDetails.componentRequirements,
                orphanSection: orphanedRequirements,
              },
              progress: checksheetData['progress'],
              lockedBy: lockedOrUnlocked,
              recalledBy: checksheetData['recalledBy']
                ? JSON.parse(checksheetData['recalledBy'])
                : {},
              courseLists: jsonDetails.courseLists,
              details: jsonDetails,
              proposed: checksheetData['proposed'] ? true : false,
            })
          );
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getChecksheetIsSuccess, checksheetData, checkId]);

  if (checkId && isPending && fetchStatus === 'fetching')
    return <div className="container">Loading...</div>;

  if (
    // getTemplateIsError ||
    getChecksheetIsError ||
    // fetchProgramsIsError ||
    // getValidationIsError ||
    updateChecksheetIsError
  )
    return (
      <div>
        <div>An error has occurred: {error}</div>
        <div>
          Please refresh the page. If the error persists, please try again
          later.
        </div>
      </div>
    );

  return (
    <>
      <div>
        <div
          ref={headerRef}
          className="checksheet-header bg-white border-bottom border-gray-4 position-sticky"
          style={{ zIndex: 1 }}
        >
          {checksheetData && college !== 'OTHER' && (
            <div
              className={classNames(
                isValid &&
                  !checksheetRequirementList?.orphanSection?.subsections
                    ?.orphanSubsection?.requirements?.length &&
                  ((isSameRole &&
                    progress !== 'PROVOST' &&
                    progress !== 'DARS') ||
                    progress === 'COMPLETE')
                  ? 'bg-success-custom'
                  : 'bg-gray-1 border-bottom border-gray-4',
                'd-flex py-1'
              )}
              style={{ width: '100%' }}
            >
              <div className="container d-flex justify-content-between align-items-center">
                {!!Object.keys(recalledBy).length ? (
                  <div className="d-flex gap-2">
                    <div className="fw-bold" style={{ padding: '5px 0' }}>
                      Recall request pending review
                    </div>
                    {currentTab !== 2 && (
                      <Button
                        label="View request"
                        color="gold"
                        size="small"
                        onClick={() => setCurrentTab(2)}
                      />
                    )}
                  </div>
                ) : (
                  <>
                    <div className="d-flex gap-2">
                      {isValid &&
                      !checksheetRequirementList?.orphanSection?.subsections
                        ?.orphanSubsection?.requirements?.length ? (
                        progress === 'COMPLETE' ? (
                          <div className="fw-bold" style={{ padding: '5px 0' }}>
                            The checksheet is complete!
                          </div>
                        ) : !!progress && !isSameRole ? (
                          <div className="fw-bold" style={{ padding: '5px 0' }}>
                            The checksheet is pending{' '}
                            {StaticData.progressRoles[progress]} review.
                          </div>
                        ) : userIsProvost || userIsDars ? (
                          <div className="fw-bold" style={{ padding: '5px 0' }}>
                            This checksheet is pending approval.
                          </div>
                        ) : !!nextReview ? (
                          <div
                            className="d-flex gap-1 align-items-center"
                            style={{ padding: '5px 0' }}
                          >
                            <FontAwesomeIcon
                              icon={faCheckCircle}
                              className="text-success-custom"
                            />
                            <div className="fw-bold">
                              Checksheet satisfies all rules and may be
                              submitted for{' '}
                              {StaticData.progressRoles[nextReview]} review.
                            </div>
                          </div>
                        ) : (
                          <div className="fw-bold" style={{ padding: '5px 0' }}>
                            This checksheet has been completed
                          </div>
                        )
                      ) : (
                        <div className="fw-bold" style={{ padding: '5px 0' }}>
                          This checksheet does not meet the validation
                          requirements
                        </div>
                      )}
                      {!Object.keys(recalledBy).length &&
                        !!previousReview &&
                        ((userIsProvost && progress === 'DARS') ||
                          (userIsCollege &&
                            isSameCollege &&
                            (progress === 'PROVOST' || progress === 'DARS')) ||
                          (userIsDepartment &&
                            isSameDepartment &&
                            (progress === 'COLLEGE' ||
                              progress === 'PROVOST' ||
                              progress === 'DARS'))) && (
                          <Button
                            label={
                              userIsProvost ? 'Recall' : 'Request a recall'
                            }
                            color="dark"
                            size="small"
                            onClick={() => handleOpenModal('recall')}
                          />
                        )}
                    </div>
                    <div className="d-flex gap-2 align-items-center">
                      {isSameRole &&
                        !!previousReview &&
                        isLocked &&
                        holdsLock &&
                        (userIsProvost ||
                          ((userIsCollege || userIsDars) && isSameCollege)) && (
                          <div
                            className="button-link"
                            onClick={() => handleOpenModal('return')}
                            role="button"
                          >
                            Return checksheet
                          </div>
                        )}
                      {isSameRole &&
                        !!nextReview &&
                        isValid &&
                        !checksheetRequirementList?.orphanSection?.subsections
                          ?.orphanSubsection?.requirements?.length &&
                        (isLocked && holdsLock ? (
                          (progress === 'PROVOST' || progress === 'DARS') &&
                          !proposed ? (
                            <Button
                              label="Approve"
                              color="gold"
                              size="small"
                              icon={['fa', 'check']}
                              onClick={() => handleOpenModal('submit')}
                            />
                          ) : (
                            (progress === 'COLLEGE' ||
                              progress === 'DEPARTMENT') && (
                              <Button
                                label="Submit"
                                color="dark"
                                size="small"
                                onClick={() => handleOpenModal('submit')}
                              />
                            )
                          )
                        ) : (
                          <div>
                            Checksheet must be locked before{' '}
                            {progress === 'PROVOST' || progress === 'DARS'
                              ? 'approval'
                              : 'submission'}
                          </div>
                        ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
          <div className="container mt-2">
            <div className="row">
              <div className="col-4">
                {readOnly && checksheetData && (
                  <div className="text-maroon">(Read Only)</div>
                )}
                {editName ? (
                  <div>
                    <div
                      className="position-relative"
                      style={{ width: 'fit-content' }}
                    >
                      <input
                        value={nameInput}
                        onChange={(e) => setNameInput(e.target.value)}
                        className="mb-1 pe-14"
                        style={{ width: '600px' }}
                      />
                      <span
                        className="position-absolute top-0 end-0 text-gray-5"
                        style={{ margin: '0.4rem 0.5rem' }}
                      >
                        (PROPOSED)
                      </span>
                    </div>
                    <div className="d-flex gap-1 mb-1">
                      <Button
                        label="Save"
                        size="xsmall"
                        color="maroon"
                        onClick={handleSaveNameChange}
                      />
                      <Button
                        label="Cancel"
                        size="xsmall"
                        onClick={handleCancelNameChange}
                      />
                    </div>
                  </div>
                ) : proposed &&
                  (userIsProvost ||
                    (userIsCollege && isSameCollege) ||
                    (userIsDepartment && isSameDepartment)) &&
                  isLocked &&
                  holdsLock ? (
                  <div
                    className="fs-4 fw-bold underline-hover"
                    style={{ width: 'fit-content' }}
                    onClick={() => setEditName(true)}
                    role="button"
                  >
                    {checksheetName}
                    {proposed && (
                      <span>
                        {' '}
                        (PROPOSED)
                        <FontAwesomeIcon
                          icon={faPenToSquare}
                          className="text-maroon"
                          style={{ width: '16px', marginLeft: '8px' }}
                        />
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="fs-4 fw-bold">
                    {proposed
                      ? checksheetName
                      : `${
                          !!program
                            ? // acadplan !== 'LSBISBIS' ?
                              program.acadPlanMarketingDescription
                            : // : subplanName
                              null
                        }${
                          // acadplan !== 'LSBISBIS' ?
                          degree
                          // : ''
                        }`}
                    {proposed && <span> (PROPOSED)</span>}
                  </div>
                )}
                {!!subplanName && (
                  // acadplan !== 'LSBISBIS' &&
                  <div className="fw-bold mb-1">
                    <span>{subplanName}</span>
                    <span className="subplan-tag ms-1">Subplan</span>
                  </div>
                )}
                {/* {acadplan === 'LSBISBIS' && (
                  <div className="fw-bold mb-1">
                    <span className="subplan-tag">
                      Interdisciplinary Studies
                    </span>
                  </div>
                )} */}
                <div style={{ fontSize: '14px' }}>
                  <span key={college}>
                    <span>
                      {college &&
                        (college !== 'OTHER' ? (
                          <span>
                            {!!collegeList[college]
                              ? collegeList[college].description
                              : '\u2014'}{' '}
                            /{' '}
                          </span>
                        ) : (
                          <span>Other / </span>
                        ))}
                      {/* {owner.departmentDescription} ({owner.departmentAcadOrg}) */}
                      <span>
                        {acadplan}
                        {!program && ' (temporary)'} /{' '}
                      </span>
                      {paramAcadSubplan !== 'null' && (
                        <span>{paramAcadSubplan} / </span>
                      )}
                    </span>
                  </span>
                  <span>{year.label}</span>
                </div>
              </div>
              <div className="col-8">
                <div>
                  <div
                    className="d-flex gap-3 align-items-center justify-content-end mb-1"
                    style={{ padding: '6px 0' }}
                  >
                    {progress && (
                      <div className="d-flex gap-1 align-items-center fw-bold">
                        <FontAwesomeIcon
                          icon={faCircle}
                          className={classNames(statusCircle[progress].color)}
                          width={'16px'}
                        />
                        <div>{StaticData.statusArray[progress]}</div>
                      </div>
                    )}
                    {lockedBy && !!Object.keys(lockedBy).length && (
                      <div className="d-flex gap-1 align-items-center bg-gray-2 px-1 rounded-1">
                        <FontAwesomeIcon
                          icon={faLock}
                          className="text-gray-6"
                          style={{ width: '12px' }}
                        />
                        <div>Locked by {lockedBy.asurite}</div>
                      </div>
                    )}
                  </div>
                  <div className="d-flex gap-3 align-items-center justify-content-end">
                    <div
                      className="d-flex gap-1 align-items-center"
                      style={{ color: 'maroon' }}
                    >
                      <a
                        href={`/checksheet-catalog-changes?id=${checksheetId}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Check for catalog changes
                      </a>
                    </div>

                    {((proposed &&
                      ((userIsCollege && isSameCollege) ||
                        (userIsDepartment && isSameDepartment))) ||
                      userIsProvost) && (
                      <div
                        className="button-link d-flex gap-1 align-items-center"
                        onClick={() => handleSubmitById(checksheetId)}
                        role="button"
                      >
                        Submit to Kuali
                      </div>
                    )}

                    <Button
                      label="Preview"
                      size="small"
                      color="maroon"
                      icon={['fa', 'external-link']}
                      style={{ color: 'white' }}
                      href={`${previewUrl}/${year.value}/${college}/${acadplan}/${acadsubplan}`}
                      target="_blank"
                    />

                    {!Object.keys(recalledBy).length && (
                      <>
                        {isLocked && (holdsLock || userIsProvost) && (
                          <Button
                            label="Unlock"
                            size="small"
                            icon={['fa', 'unlock']}
                            classes={[
                              'btn btn-white btn-md border border-maroon text-maroon',
                            ]}
                            onClick={() => handleLockUnlock('unlock')}
                            disabled={preventLock}
                          />
                        )}

                        {!isLocked &&
                          !!progress &&
                          isSameRole &&
                          (userIsProvost ||
                            ((userIsCollege || userIsDars) && isSameCollege) ||
                            (userIsDepartment && isSameDepartment)) && (
                            <Button
                              label="Lock"
                              size="small"
                              color="maroon"
                              icon={['fa', 'lock']}
                              onClick={() => handleLockUnlock('lock')}
                              disabled={preventLock}
                            />
                          )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="d-flex gap-3">
              {checksheetData &&
                componentIdNames.map((componentIdName, index) => (
                  <a
                    key={index}
                    href={`#${componentIdName.componentId}`}
                    onClick={(e) => {
                      e.preventDefault();

                      const element = document.getElementById(
                        componentIdName.componentId
                      );

                      if (element) {
                        const headerHeight = headerRef.current
                          ? headerRef.current.offsetHeight +
                            (index === 0 ? 180 : activeTab === 0 ? 170 : 120)
                          : 0;
                        const elementPosition =
                          element.getBoundingClientRect().top;
                        const offsetPosition =
                          elementPosition + window.pageYOffset - headerHeight;

                        window.scrollTo({
                          top: offsetPosition,
                          behavior: 'smooth',
                        });
                      }

                      setActiveTab(index);
                    }}
                    className={classNames(
                      activeTab === index
                        ? 'active-tab fw-bold'
                        : 'inactive-tab',
                      'pt-2 pb-1 d-flex justify-content-center text-gray-7 text-decoration-none'
                    )}
                  >
                    {componentIdName.componentName}
                  </a>
                ))}
            </div>
          </div>
        </div>
        {/* TODO: Might need to adjust padding based off of height of header, I need to think about this */}
        <div className="container">
          <div className="row no-gutters">
            <div className="col uds-full-width">
              {!templateNotFound ? (
                <>
                  <div className="pt-2">
                    <DndContext
                      sensors={sensors}
                      collisionDetection={pointerWithin}
                      onDragStart={handleDragStart}
                      onDragEnd={handleDragEnd}
                      onDragOver={handleDragOver}
                      modifiers={[restrictToVerticalAxis]}
                    >
                      <DragOverlay
                        style={{
                          width: '50%',
                        }}
                        dropAnimation={null}
                      >
                        {showOverlay ? (
                          <div className="px-4 py-2 fw-bold me-4 d-flex bg-white border border-gray1">
                            {activeName}
                          </div>
                        ) : null}
                      </DragOverlay>
                      {!!checksheetRequirementList?.orphanSection?.subsections
                        ?.orphanSubsection?.requirements?.length && (
                        <OrphanedRequirements
                          orphans={checksheetRequirementList.orphanSection}
                          year={year.value}
                          checksheet={true}
                          refetchChecksheet={refetchChecksheet}
                          validation={validation}
                          refetchValidation={refetchValidation}
                          isLocked={isLocked && !userIsDars}
                          holdsLock={holdsLock}
                          openEditModal={openEditModal}
                          updateData={updateData}
                          dataToModal={dataToModal}
                          // dataToParent={(newData) =>
                          //   receiveDataFromComponent(newData)
                          // }
                          updateSubsection={updateSubsection}
                          readOnly={readOnly}
                          history={historyRequirements}
                          historySubsections={historySubsections}
                        />
                      )}
                      {templateComponentIds.map((componentId) => (
                        <div
                          key={componentId}
                          className="mb-9"
                          id={componentId}
                        >
                          <Component
                            id={componentId}
                            year={year.value}
                            checksheet={true}
                            validation={validation}
                            refetchValidation={refetchValidation}
                            isLocked={isLocked && !userIsDars}
                            holdsLock={holdsLock}
                            openEditModal={openEditModal}
                            updateData={updateData}
                            dataToModal={dataToModal}
                            // dataToParent={(newData) =>
                            //   receiveDataFromComponent(newData)
                            // }
                            updateSubsection={updateSubsection}
                            readOnly={readOnly}
                            history={historyRequirements}
                            historyDeleted={historyDeleted}
                            historySubsections={historySubsections}
                          />
                        </div>
                      ))}
                    </DndContext>
                  </div>
                </>
              ) : (
                <div>
                  The template associated with this checksheet was not found.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <Modal show={showModal} onHide={handleCloseModal}>
        <ProgressUpdateForm
          view={modalView}
          progress={progress}
          updatedProgress={
            modalView === 'recall' || modalView === 'return'
              ? previousReview
              : modalView === 'submit'
                ? nextReview
                : ''
          }
          college={college}
          department={department}
          mainOwner={mainOwner}
          handleProgressUpdate={handleProgressUpdate}
          handleRequestRecall={handleRequestRecall}
          handleClose={handleCloseModal}
          role={role}
        />
      </Modal>

      {/* Modal for the Kuali submission */}
      <Modal show={showKualiModal} onHide={handleCloseModal} centered>
        <div style={{ width: '400px', maxWidth: '100%' }}>
          <div className="p-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                {kualiSubmitting ? (
                  // Block 1: Submitting
                  <div className="text-danger-custom fw-bold">
                    Submitting...
                  </div>
                ) : // Block 2: Submitted (Success or Error)
                kualiStatus === 'SUCCESS' ? (
                  <div>
                    <span className="text-success-custom">
                      <FontAwesomeIcon icon={faCheckCircle} />
                    </span>{' '}
                    Successfully submitted to Kuali
                  </div>
                ) : (
                  <>
                    <div>
                      <span className="text-danger-custom">
                        <FontAwesomeIcon icon={faTriangleExclamation} />
                      </span>{' '}
                      Failed to submit to Kuali
                    </div>
                    <div style={{ fontSize: '12px' }}>{kualiStatus}</div>
                  </>
                )}
              </div>
            </div>

            <div>
              {!kualiSubmitting &&
                (kualiStatus !== 'SUCCESS' ? (
                  <div className="d-flex justify-content-end gap-2">
                    <Button
                      label="Retry"
                      size="small"
                      color="gold"
                      icon={['fas', 'rotate-left']}
                      onClick={() => handleSubmitById(checksheetId)}
                    />

                    <Button
                      label="Close"
                      size="small"
                      color="dark"
                      style={{ color: 'white' }}
                      onClick={handleCloseModal}
                    />
                  </div>
                ) : (
                  <Button
                    label="Close"
                    size="small"
                    color="maroon"
                    style={{ color: 'white' }}
                    onClick={handleCloseModal}
                  />
                ))}
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ChecksheetForm;
